import { z } from "zod";

import { AssignStatus, InclusionStatus } from "@/enum/list.enum";

/**
 * The lead panel's Profiles tab: the colleagues of the open lead — every prospect of this company's
 * lists who works at the same organization — with the memberships, channels and persona verdict the
 * cards render. Served by `GET list_person/:id/organization-profiles`.
 *
 * Split out of `schema/list.ts` (PREC-4116), which is over the length ratchet and may only shrink.
 */
export const OrganizationProfileListSchema = z.object({
	listId: z.number(),
	listName: z.string(),
	listPersonId: z.number(),
	assignStatus: z.nativeEnum(AssignStatus),
	inclusionStatus: z.nativeEnum(InclusionStatus),
});

export const OrganizationProfileSchema = z.object({
	prospectId: z.number(),
	name: z.string().nullable(),
	title: z.string().nullable(),
	image: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	email: z.string().nullable(),
	emailStatus: z.string().nullable(),
	phone: z.string().nullable(),
	phoneStatus: z.string().nullable(),
	/**
	 * Persona priority of the company's current scoring run (`1` / `2` / `3` / `Disqualified` /
	 * `Unsure`) behind the colour-coded badge. Optional because the endpoint omits it for a company
	 * that has never been scored — which is not the same as a lead the run could not place, and the
	 * badge shows the two differently.
	 */
	overallScore: z.string().nullish(),
	overallReasoning: z.string().nullish(),
	isOnCurrentList: z.boolean(),
	lists: z.array(OrganizationProfileListSchema),
});

export const OrganizationProfilesResponseSchema = z.object({
	organizationId: z.number(),
	organizationName: z.string(),
	profiles: z.array(OrganizationProfileSchema),
});

export type OrganizationProfile = z.infer<typeof OrganizationProfileSchema>;
export type OrganizationProfileList = z.infer<typeof OrganizationProfileListSchema>;
export type OrganizationProfilesResponse = z.infer<typeof OrganizationProfilesResponseSchema>;
